'use client';
/* ── ALIQUOTS DE CONTRÔLE — vue d'ensemble (RQ, 03/10/2026) ─────────────────
 *
 * L'onglet « Aliquots » des matériaux reconstitués : ce qui reste au
 * congélateur, produit par produit et niveau par niveau ; l'aliquot à prendre
 * en premier ; la rupture prévue au rythme de passage ; ce qui est à jeter ; et
 * deux gestes — reconstituer un flacon (qui crée et numérote ses aliquots) et
 * imprimer des étiquettes (Zebra ou planche A4).
 *
 * Le geste QUOTIDIEN — sortir un tube — passe par la commande flottante
 * « Déstocker un aliquot », présente sur tous les écrans. Cette vue en est le
 * tableau de bord, pas le poste.
 *
 * Tout ce qui se décide (dates limites, premier périmé, ruptures) est calculé
 * par le moteur ; cet écran affiche. */
import React, { useEffect, useMemo, useState } from 'react';
import { fmtDate, aujourdhuiIso } from '@/lib/meta';
import { niveauCourt, instantLabo } from '@/lib/aliquots-affichage';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed } from '@/lib/store';
import { card, inputStyle, Modale, Skeleton, EmptyState, I } from '@/components/ui';
import { positionsPlanche, barres128 } from '@/lib/planche-etiquettes';
import { DestockageFenetre } from '@/components/DestockageFenetre';
import { imprimanteDuPoste, imprimerSurLePoste, LIEN_BROWSER_PRINT, type Detection } from '@/lib/zebra-poste';
import type { AliquotVue, EtatAliquots, LotVue, ModeleFlacon, ProduitVue, ProximiteProduit, StatutAliquot } from '@/components/aliquots-types';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { Segment } from '@/shared/Segment';
const STATUTS: Record<StatutAliquot, { label: string; couleur: string }> = {
  congele: { label: 'congelé', couleur: 'var(--cyan-700)' },
  decongele: { label: 'décongelé', couleur: 'var(--amber-700)' },
  utilise: { label: 'utilisé', couleur: 'var(--text-muted)' },
  jete: { label: 'jeté', couleur: 'var(--red-600)' },
};

const ALERTE = {
  rupture: { fond: 'var(--red-50)', encre: 'var(--red-700)' },
  alerte: { fond: 'var(--amber-50)', encre: 'var(--amber-800)' },
  ok: { fond: 'transparent', encre: 'var(--text-body)' },
  inconnu: { fond: 'var(--gray-25)', encre: 'var(--text-muted)' },
};

const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 };
/** Un bouton désactivé doit SE VOIR désactivé, et la raison s'écrire à côté
 *  (le piège du « bouton grisé sans phrase », déjà payé deux fois). */
const aide: React.CSSProperties = { fontSize: 12, color: 'var(--text-muted)' };
const jjmmaa = (j: string) => (/^\d{4}-\d{2}-\d{2}/.test(j) ? `${j.slice(8, 10)}/${j.slice(5, 7)}/${j.slice(2, 4)}` : '—');

export function Aliquots() {
  const { session, showToast } = useGed();
  const [etat, setEtat] = useState<EtatAliquots | null>(null);
  const [chargement, setChargement] = useState(true);
  const [version, setVersion] = useState(0);
  const [filtre, setFiltre] = useState<'vivants' | StatutAliquot | 'tous'>('vivants');
  const [q, setQ] = useState('');
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [reconstitution, setReconstitution] = useState(false);
  const [etiquetage, setEtiquetage] = useState<LotVue[] | null>(null);
  const [rebut, setRebut] = useState<AliquotVue[] | null>(null);
  const [destockage, setDestockage] = useState(false);

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/aliquots', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivant) setEtat(d); })
      .catch(() => { if (vivant) setEtat(null); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [version]);
  const recharger = () => { setSelection(new Set()); setVersion((n) => n + 1); };

  /* ── LA LISTE EST CELLE DES LOTS (RQ, 05/10/2026) ───────────────────────
   *
   * « Dans la liste des aliquots, je veux voir le nom, la date de
   * reconstitution et le nombre d'aliquots restants. » Les tubes n'ont plus
   * d'identité : un lot, une ligne, un compte.
   *
   * ⚠️ LES TUBES DEHORS GARDENT LEUR LIGNE À PART (plus bas). Un tube
   * décongelé est en cours d'usage, avec sa propre horloge de stabilité après
   * décongélation : le fondre dans le compte du lot ferait disparaître
   * l'échéance qui court. Il s'y désigne « un tube du lot X », jamais par un
   * numéro. */
  const lots = useMemo(() => {
    const tous = etat?.lots || [];
    const base = filtre === 'congele' || filtre === 'vivants' ? tous.filter((l) => l.restants > 0) : tous;
    const qq = q.trim().toLowerCase();
    return qq ? base.filter((l) => `${l.produit} ${l.numeroLot} n${l.niveau} ${l.flaconRef} ${l.codeLot}`.toLowerCase().includes(qq)) : base;
  }, [etat, filtre, q]);
  const dehors = useMemo(() => {
    const tous = (etat?.aliquots || []).filter((a) => a.statut === 'decongele');
    const qq = q.trim().toLowerCase();
    return qq ? tous.filter((a) => `${a.produit} ${a.numeroLot} n${a.niveau} ${a.flaconRef}`.toLowerCase().includes(qq)) : tous;
  }, [etat, q]);

  // Un état vide est une AFFIRMATION : tant que la lecture n'est pas revenue,
  // on ne dit pas « aucun aliquot ».
  if (chargement && !etat) return <div style={{ ...card, padding: 0 }}><Skeleton rows={5} /></div>;
  if (!etat) {
    return <div style={{ ...card, padding: 16, fontSize: 13, color: 'var(--red-600)' }}>Lecture des aliquots impossible — rechargez la page. Rien n’est affiché plutôt qu’un stock faux.</div>;
  }
  const parCode = new Map(etat.aliquots.map((a) => [a.code, a]));
  /* Les LOTS sélectionnés — c'est sur eux que portent l'étiquetage et le
     rebut. `choisis` (les tubes congelés de ces lots) ne sert plus qu'au
     rebut, qui reste un geste sur des tubes : on jette N tubes, pas un lot. */
  const lotsChoisis = (etat.lots || []).filter((l) => selection.has(l.flaconRef));
  const choisis = etat.aliquots.filter((a) => a.statut === 'congele' && selection.has(a.flaconRef));

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {etat.peutDeclarer && (
          <Bouton variante="primaire" taille="sm" onClick={() => setReconstitution(true)}>
            <I n="plus" s={13} />Reconstituer un flacon
          </Bouton>
        )}
        {etat.peutDeclarer && (
          <Bouton taille="sm" onClick={() => setDestockage(true)}>
            <I n="tube" s={13} />Déstocker
          </Bouton>
        )}
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Le déstockage quotidien se fait aussi depuis n’importe quel écran : rond « tube » en bas à droite.
        </span>
      </div>

      {etat.etiquettes.aConfirmer.length > 0 && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, lineHeight: 1.5 }}>
          <strong>Étiquetage à confirmer :</strong> {etat.etiquettes.aConfirmer.join(' ; ')}. Paramétrage Qualité → Validité des matériaux.
        </div>
      )}

      {/* ── Stock par produit et niveau ── */}
      <div style={{ ...card, padding: 16, display: 'grid', gap: 10 }}>
        <strong style={{ fontSize: 14 }}>Au congélateur, par produit</strong>
        {etat.produits.filter((p) => p.niveaux.some((n) => n.congeles + n.decongeles > 0) || p.reglage).length === 0 && (
          <EmptyState titre="Aucun aliquot n’a encore été créé."
            indice="Les flacons repris des classeurs n’ont pas d’aliquots : leur consommation passée n’est connue nulle part. Les aliquots naissent à la prochaine reconstitution." />
        )}
        {etat.produits.filter((p) => p.niveaux.some((n) => n.congeles + n.decongeles > 0) || p.reglage).map((p) => <LigneProduit key={p.cle} p={p} />)}
      </div>

      {/* ── À jeter ── */}
      {etat.aJeter.length > 0 && (
        <div style={{ ...card, padding: 16, display: 'grid', gap: 8, borderInlineStart: '4px solid var(--red-500)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <strong style={{ fontSize: 14, color: 'var(--red-700)' }}>{etat.aJeter.length} aliquot{etat.aJeter.length > 1 ? 's' : ''} à jeter</strong>
            <Bouton variante="danger" taille="sm" onClick={() => setRebut(etat.aJeter.map((x) => parCode.get(x.code)).filter((x): x is AliquotVue => !!x))} style={{ marginInlineStart: 'auto' }}>
              Jeter ces aliquots…
            </Bouton>
          </div>
          {etat.aJeter.map((x) => (
            <div key={x.code} style={{ fontSize: 12.5 }}>
              <strong style={{ fontFamily: 'var(--font-mono)' }}>{parCode.get(x.code)?.codeLisible || x.code}</strong>
              {' '}{parCode.get(x.code)?.produit} {niveauCourt(parCode.get(x.code)?.niveau)} — {x.raison}
            </div>
          ))}
        </div>
      )}

      {/* ── Liste des aliquots ── */}
      <div style={{ ...card, padding: 16, display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Segment ariaLabel="Filtre" valeur={filtre} onChange={setFiltre} options={(['vivants', 'congele', 'decongele', 'utilise', 'jete', 'tous'] as const).map((k) => ({ valeur: k, libelle: k === 'vivants' ? 'En stock' : k === 'tous' ? 'Tous (90 j)' : STATUTS[k].label }))} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code, produit, lot, flacon…"
            aria-label="Filtrer les aliquots" style={{ ...inputStyle, height: 30, minWidth: 200, fontSize: 12.5 }} />
          {selection.size > 0 && (
            <span style={{ marginInlineStart: 'auto', display: 'inline-flex', gap: 8 }}>
              <Bouton variante="primaire" taille="sm" onClick={() => setEtiquetage(lotsChoisis)}>
                <I n="print" s={13} />Étiquettes ({selection.size})
              </Bouton>
              <Bouton taille="sm" onClick={() => setRebut(choisis.filter((a) => a.statut === 'congele' || a.statut === 'decongele'))}>
                Jeter…
              </Bouton>
            </span>
          )}
        </div>
        {lots.length === 0 && dehors.length === 0 && (
          <EmptyState titre={(etat.lots || []).length ? 'Aucun lot ne correspond.' : 'Aucun aliquot enregistré.'} />
        )}
        {lots.map((l) => <LigneLot key={l.flaconRef} l={l}
          choisi={selection.has(l.flaconRef)}
          onChoisir={(v) => setSelection((s) => { const n = new Set(s); if (v) n.add(l.flaconRef); else n.delete(l.flaconRef); return n; })} />)}

        {/* ── Les tubes DEHORS : leur horloge court, on les suit un par un ──
            Sans identité : « un tube du lot X », jamais un numéro. */}
        {dehors.length > 0 && (
          <div style={{ marginTop: 6, paddingTop: 8, borderTop: '1px solid var(--border-default)', display: 'grid', gap: 6 }}>
            <strong style={{ fontSize: 12.5 }}>Tubes sortis du congélateur ({dehors.length})</strong>
            {dehors.map((a) => (
              <div key={a.documentId} style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }}>
                <span style={{ minWidth: 200 }}>un tube de <strong>{a.produit}</strong> · {niveauCourt(a.niveau)}</span>
                <span style={{ fontFamily: 'var(--font-mono)' }}>lot {a.numeroLot || '—'}</span>
                <span style={{ color: 'var(--text-muted)' }}>reconstitué le {fmtDate(a.dateReconstitution)}</span>
                <span style={{ flex: 1 }} />
                <span title={a.manques.join(' ; ') || undefined} style={{ color: a.dansStabilite === false ? 'var(--red-600)' : 'var(--text-body)', fontWeight: a.dansStabilite === false ? 700 : 400 }}>
                  {a.dansStabilite === false ? `périmé : ${a.causeLibelle} — ` : ''}
                  {a.limiteDecongele ? `jusqu’au ${instantLabo(a.limiteDecongele)}` : 'limite inconnue'}
                  {a.dansStabilite !== false && a.causeLibelle ? ` · ${a.causeLibelle}` : ''}{a.manques.length ? ' *' : ''}
                  {a.destockageMode === 'manuel' && <em style={{ color: 'var(--amber-700)' }}> · sans scan</em>}
                  {a.decongeleParNom && <span style={{ color: 'var(--text-muted)' }}> · déstocké par {a.decongeleParNom}</span>}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {reconstitution && (
        <Reconstituer produits={etat.produits} aujourdhui={etat.aujourdhui} moi={session.key}
          disciplines={etat.disciplines || []} rythmes={etat.rythmes || []}
          onClose={() => setReconstitution(false)}
          onFait={async (codes) => {
            setReconstitution(false);
            // Les aliquots neufs, relus pour l'étiquetage qui suit.
            const r = await fetch('/api/ged/ged/aliquots', { credentials: 'include' });
            const d: EtatAliquots | null = r.ok ? await r.json() : null;
            if (d) setEtat(d);
            /* Le LOT qu'on vient de créer, retrouvé par un de ses tubes : on
               étiquette un lot, plus une liste de tubes. */
            const refNeuve = (d?.aliquots || []).find((a) => codes.includes(a.code))?.flaconRef || '';
            const lotNeuf = (d?.lots || []).find((l) => l.flaconRef === refNeuve);
            if (lotNeuf) setEtiquetage([lotNeuf]);
            showToast(`Flacon reconstitué — ${codes.length} aliquot(s) créé(s).`);
          }} />
      )}
      {etiquetage && (
        <Etiqueter lots={etiquetage} etat={etat} onClose={() => { setEtiquetage(null); recharger(); }} />
      )}
      {rebut && <Jeter aliquots={rebut} onClose={() => setRebut(null)} onFait={() => { setRebut(null); recharger(); }} />}
      {destockage && <DestockageFenetre onClose={() => { setDestockage(false); recharger(); }} />}
    </div>
  );
}

/**
 * UNE LIGNE DE LOT — ce que la RQ veut voir (05/10/2026) : « le nom, la date
 * de reconstitution et le nombre d'aliquots restants ».
 *
 * ⚠️ AUCUN NUMÉRO DE TUBE. Les tubes d'un lot sont interchangeables : les
 * numéroter à l'écran inviterait à aller chercher « le n° 7 », qui n'a aucune
 * raison d'être préféré au n° 3. Le lot est l'unité de décision ; le tube n'est
 * qu'un support de faits, en base.
 *
 * ⚠️ « 0 restant » SE MONTRE, il ne se cache pas : un lot épuisé dont des tubes
 * sont encore dehors reste à l'écran, sinon la disparition de la ligne se
 * lirait « il n'y a plus rien de ce produit » alors qu'un tube est en train de
 * servir.
 */
function LigneLot({ l, choisi, onChoisir }: { l: LotVue; choisi: boolean; onChoisir: (v: boolean) => void }) {
  const vide = l.restants === 0;
  return (
    <label style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5, padding: '6px 0', borderTop: '1px solid var(--border-subtle)', cursor: 'pointer', opacity: vide ? 0.65 : 1 }}>
      <input type="checkbox" checked={choisi} disabled={vide}
        onChange={(e) => onChoisir(e.target.checked)} />
      <strong style={{ minWidth: 170 }}>{l.produit}</strong>
      <span style={{ minWidth: 60, fontWeight: 700, color: 'var(--text-muted)' }}>{niveauCourt(l.niveau)}</span>
      <span style={{ fontFamily: 'var(--font-mono)' }}>lot {l.numeroLot || '—'}</span>
      <span style={{ color: 'var(--text-muted)' }}>reconstitué le {fmtDate(l.dateReconstitution)}</span>
      <span style={{ fontWeight: 700, color: vide ? 'var(--text-muted)' : 'var(--text-strong)' }}
        title={`${l.crees} créé(s) · ${l.sortis} sorti(s) · ${l.jetes} jeté(s)`}>
        {l.restants} restant{l.restants > 1 ? 's' : ''} sur {l.crees}
      </span>
      <span style={{ flex: 1 }} />
      {l.limite
        ? <span>limite {fmtDate(l.limite)}{l.cause ? ` · ${l.cause === 'lot' ? 'fin du lot' : 'fin de stabilité'}` : ''}</span>
        : <span style={{ color: 'var(--amber-700)' }}>limite inconnue</span>}
      {/* Le code du lot n'est montré que pour retrouver une étiquette illisible
          — en petit, et jamais comme l'identité de la ligne. */}
      {l.codeLot && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{l.codeLot}</span>}
    </label>
  );
}

function LigneProduit({ p }: { p: ProduitVue }) {
  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 8, display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13.5 }}>{p.libelle}</strong>
        {p.reglage?.rythme && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>passage {p.reglage.rythme}</span>}
        {p.manques.length > 0 && (
          <span style={{ fontSize: 12, color: 'var(--amber-700)' }}>
            {p.manques.join(' · ')} — Paramétrage → Validité des matériaux
          </span>
        )}
      </div>
      <div style={{ display: 'grid', gap: 4 }}>
        {p.niveaux.map((n) => {
          const a = ALERTE[n.rupture?.niveauAlerte || 'inconnu'];
          return (
            <div key={n.niveau} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 12.5, background: a.fond, borderRadius: 'var(--radius-sm)', padding: '4px 8px' }}>
              <strong style={{ minWidth: 70 }}>niveau {n.niveau}</strong>
              <span>{n.congeles} congelé{n.congeles > 1 ? 's' : ''}</span>
              {n.decongeles > 0 && <span>{n.decongeles} décongelé{n.decongeles > 1 ? 's' : ''}</span>}
              {/* Le LOT à entamer d'abord, désigné par sa limite — plus par
                  un numéro de tube (06/10/2026) : dans la boîte, n'importe
                  lequel convient. */}
              {n.premier && <span>à entamer d’abord : <strong>{n.premier.limite ? `limite ${fmtDate(n.premier.limite)}` : 'le plus ancien'}</strong>{n.premier.causeLibelle ? ` · ${n.premier.causeLibelle}` : ''}</span>}
              {n.rupture && <span style={{ color: a.encre, fontWeight: n.rupture.niveauAlerte === 'ok' ? 400 : 700 }}>{n.rupture.message}</span>}
              {!n.rupture && <span style={{ color: 'var(--text-muted)' }}>aucun aliquot créé pour ce niveau</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Reconstituer un flacon : pré-rempli depuis le dernier flacon ──
 *
 * Deux sources, qui ne disent pas la même chose :
 *  · le dernier flacon du NIVEAU (lot, péremption, référence, emplacement) —
 *    on enchaîne le plus souvent sur le même lot ;
 *  · le MODÈLE (RQ, 03/10/2026) : le dernier flacon du même produit — même
 *    libellé ET même référence fournisseur — qui porte des valeurs de notice
 *    (stabilités, usage, volumes). Choisi par le moteur (`modeleDeFlacon`),
 *    et DIT à l'écran : « repris du flacon <réf.> du <date> — vérifiez sur la
 *    notice ». Ces valeurs sont celles du FLACON créé : une notice qui change
 *    sur un lot récent ne déplace pas la DLU des aliquots déjà étiquetés.
 *
 * La péremption après reconstitution n'est JAMAIS reprise : c'est une date
 * propre à ce flacon, recopiée de la notice (27/08). */
/** Même comparaison que le moteur (`cleRefFournisseur`) — elle ne sert qu'à
 *  CHOISIR parmi les modèles qu'il a servis, pas à en fabriquer. */
const cleRef = (r: string) => r.trim().toUpperCase().replace(/\s+/g, '');
const modeleDe = (pp: ProduitVue | undefined, ref: string): ModeleFlacon | null =>
  (cleRef(ref) && pp?.modeles?.find((m) => m.cleRef === cleRef(ref))) || null;
const texte = (v: number | string | null | undefined) => (v === null || v === undefined ? '' : String(v));
/* ── LES TEMPÉRATURES (RQ, 06/10/2026) ─────────────────────────────────────
 * Vocabulaire commun à l'application (backend/src/materiaux-conservation.ts).
 * ⚠️ « Où partent les aliquots » est un FAIT, pas une prescription : il se
 * reprend du flacon PRÉCÉDENT, jamais de `temperatureApres`. Le Lyphochek
 * prescrit 2 – 8 °C après reconstitution et le laboratoire congèle à −20 °C ;
 * pré-remplir l'un depuis l'autre inscrirait « 2 – 8 °C » sur des tubes
 * congelés, et effacerait l'écart qu'on cherche justement à voir. */
const TEMPERATURES: readonly (readonly [string, string])[] = [
  ['ambiante', 'Température ambiante (15 – 25 °C)'],
  ['refrigere', 'Réfrigérateur (2 – 8 °C)'],
  ['congele', 'Congélateur (−20 °C)'],
  ['tres_basse', 'Congélateur basse température (−70 °C ou moins)'],
];

const valeursNotice = (m: ModeleFlacon | null): Record<string, string> => ({
  stabiliteCongeleJours: texte(m?.valeurs.stabiliteCongeleJours),
  stabiliteDecongeleHeures: texte(m?.valeurs.stabiliteDecongeleHeures),
  usageAliquot: texte(m?.valeurs.usageAliquot),
  volumeReconstitutionUl: texte(m?.valeurs.volumeReconstitutionUl),
  // Un nombre d'unités verbatim qui n'est pas un entier (« 2x6 ») n'est pas
  // repris : le champ attend un entier, et le deviner serait inventer.
  nombreAliquots: /^\d+$/.test(texte(m?.valeurs.nombreUnites).trim()) ? texte(m?.valeurs.nombreUnites).trim() : '',
  volumeParUnite: texte(m?.valeurs.volumeParUnite),
  temperatureAvant: texte(m?.valeurs.temperatureAvant),
  temperatureApres: texte(m?.valeurs.temperatureApres),
  temperatureAliquots: texte(m?.valeurs.temperatureAliquots),
});

function champsProposes(pp: ProduitVue | undefined, nv: string, aujourdhui: string): { champs: Record<string, string>; modele: ModeleFlacon | null } {
  const dernier = (pp?.derniersFlacons?.[nv] || {}) as Record<string, string | null>;
  const refFournisseur = String(dernier.refFournisseur || '');
  const modele = modeleDe(pp, refFournisseur);
  return {
    modele,
    champs: {
      materiau: pp?.libelle || '', numeroLot: String(dernier.numeroLot || ''), peremption: String(dernier.peremption || ''),
      refFournisseur, codeBoxystock: String(dernier.codeBoxystock || ''),
      discipline: String(dernier.discipline || ''), passage: String(dernier.passage || ''),
      dateReconstitution: aujourdhui, dateOuverture: String(dernier.dateOuverture || ''),
      numeroFlacon: '',
      ...valeursNotice(modele),
      peremptionApresReconstitution: '',
      remarque: '',
    },
  };
}

/** Valeur du choix « Nouveau produit… » dans la liste des produits. */
const NOUVEAU = '__nouveau__';

function Reconstituer({ produits, aujourdhui, moi, disciplines, rythmes, onClose, onFait }: {
  produits: ProduitVue[]; aujourdhui: string; moi: string;
  disciplines: string[]; rythmes: { valeur: string; libelle: string }[];
  onClose: () => void; onFait: (codes: string[]) => void;
}) {
  const [cle, setCle] = useState(produits[0]?.cle || NOUVEAU);
  const nouveau = cle === NOUVEAU;
  const p = produits.find((x) => x.cle === cle);
  /* NOUVEAU PRODUIT (RQ, 03/10/2026). L'identité d'un produit est sa CLÉ
     (libellé normalisé par le moteur, `cleProduit`) : avant de créer, le
     moteur dit si un produit existant a la même clé ou une clé très proche
     (`produitsProches`) — deux graphies feraient deux stocks, deux alertes de
     rupture, et un FEFO qui ne verrait que la moitié des tubes. L'écran ne
     normalise RIEN lui-même. */
  const [proximite, setProximite] = useState<{ pour: string; r: ProximiteProduit } | null>(null);
  const [verification, setVerification] = useState(false);
  const [autreProduit, setAutreProduit] = useState(false);
  const [niveau, setNiveau] = useState(() => produits[0]?.niveaux[0]?.niveau || '');
  /* Le pré-remplissage se fait AU CHOIX du produit, du niveau et de la
     référence fournisseur (et à l'ouverture), jamais dans un effet :
     l'utilisateur garde la main sur ce qu'il a déjà corrigé. */
  const initial = useMemo(() => champsProposes(produits[0], produits[0]?.niveaux[0]?.niveau || '', aujourdhui), [produits, aujourdhui]);
  const [champs, setChamps] = useState<Record<string, string>>(initial.champs);
  const [modele, setModele] = useState<ModeleFlacon | null>(initial.modele);
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const preremplir = (pp: ProduitVue | undefined, nv: string) => {
    const r = champsProposes(pp, nv, aujourdhui);
    setChamps(r.champs); setModele(r.modele);
  };
  /* Changer de référence fournisseur, c'est changer de produit : le modèle
     suit. Sans modèle pour la nouvelle référence, les valeurs reprises de
     l'ANCIEN sont vidées (elles décriraient une autre boîte) ; des valeurs
     tapées à la main, sans modèle, sont laissées. */
  const changerRef = (ref: string) => {
    const m = modeleDe(p, ref);
    setChamps((x) => ({ ...x, refFournisseur: ref, ...(m || modele ? valeursNotice(m) : {}) }));
    setModele(m);
  };
  const c = (k: string) => champs[k] ?? '';
  const poser = (k: string, v: string) => setChamps((x) => ({ ...x, [k]: v }));
  const choisirProduit = (valeur: string) => {
    setCle(valeur); setProximite(null); setAutreProduit(false);
    if (valeur === NOUVEAU) {
      // Rien à reprendre : aucun flacon ne porte encore ce produit.
      const vide = champsProposes(undefined, '', aujourdhui);
      setNiveau(''); setChamps(vide.champs); setModele(null);
      return;
    }
    const pp = produits.find((x) => x.cle === valeur);
    const nv = pp?.niveaux[0]?.niveau || '';
    setNiveau(nv); preremplir(pp, nv);
  };
  const libelleNouveau = nouveau ? c('materiau').trim() : '';
  useEffect(() => {
    if (!libelleNouveau) return undefined;
    let vivant = true;
    const minuterie = window.setTimeout(() => {
      setVerification(true);
      void fetch(`/api/ged/ged/aliquots/produits-proches?libelle=${encodeURIComponent(libelleNouveau)}`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d: ProximiteProduit | null) => { if (vivant) { setProximite(d ? { pour: libelleNouveau, r: d } : null); setAutreProduit(false); } })
        .catch(() => { if (vivant) setProximite(null); })
        .finally(() => { if (vivant) setVerification(false); });
    }, 300);
    return () => { vivant = false; window.clearTimeout(minuterie); };
  }, [libelleNouveau]);
  // La vérification doit porter sur le libellé ACTUEL : une réponse pour
  // « Nortro » ne vaut pas pour « Nortrol ».
  const proxActuelle = proximite && libelleNouveau && proximite.pour === libelleNouveau ? proximite.r : null;
  const blocageNouveau = !nouveau ? ''
    : !libelleNouveau ? 'Nommez le nouveau produit.'
      : verification || !proxActuelle ? 'Vérification des produits existants…'
        : proxActuelle.identique ? `Ce produit existe déjà : choisissez « ${proxActuelle.identique.libelle} ».`
          : proxActuelle.proches.length && !autreProduit ? 'Un produit proche existe : choisissez-le, ou confirmez qu’il s’agit d’un autre produit.'
            : '';
  const blocage = blocageNouveau || (!c('materiau') ? 'Choisissez le produit.' : '');
  const envoyer = async () => {
    setErreur(''); setEnvoi(true);
    try {
      const r = await fetchEcriture('/api/ged/ged/aliquots/reconstituer', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...champs, niveau, nombreAliquots: Number(c('nombreAliquots') || 0), personnel: moi, reprisDe: modele?.ref || undefined,
          ...(nouveau ? { nouveauProduit: true, nouveauConfirme: autreProduit } : {}),
        }),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok) { setErreur(d?.error?.message || 'Reconstitution refusée.'); return; }
      onFait(d.codes || []);
    } finally { setEnvoi(false); }
  };

  const champ = (k: string, label: string, type = 'text', large = false) => (
    <div style={{ gridColumn: large ? '1 / -1' : undefined }}>
      <label style={lbl} htmlFor={`rec-${k}`}>{label}</label>
      <input id={`rec-${k}`} type={type} value={c(k)} onChange={(e) => poser(k, e.target.value)} style={{ ...inputStyle, width: '100%' }} />
    </div>
  );
  return (
    <Modale titre="Reconstituer un flacon" largeur={680} onClose={onClose}
      sousTitre="Le flacon et ses aliquots sont créés ensemble, congelés, prêts à étiqueter."
      pied={<>
        {blocage && <span style={{ ...aide, marginInlineEnd: 'auto' }}>{blocage}</span>}
        <PiedActions taille="sm" annuler={{ onClick: onClose }}
          principale={{ libelle: `Créer ${c('nombreAliquots') || 0} aliquot(s)`, libelleChargement: 'Création…', chargement: envoi, disabled: envoi || !!blocage, title: blocage || undefined, onClick: () => void envoyer() }} />
      </>}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl} htmlFor="rec-produit">Produit de contrôle</label>
          <select id="rec-produit" value={cle} style={{ ...inputStyle, width: '100%' }}
            onChange={(e) => choisirProduit(e.target.value)}>
            {produits.map((x) => <option key={x.cle} value={x.cle}>{x.libelle}</option>)}
            <option value={NOUVEAU}>Nouveau produit…</option>
          </select>
          {p && p.manques.length > 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Paramétrage : {p.manques.join(', ')} (la rupture ne sera pas prévue).</div>}
        </div>
        {nouveau && (
          <>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl} htmlFor="rec-materiau">Libellé du nouveau produit (tel qu’écrit sur la boîte)</label>
              <input id="rec-materiau" value={c('materiau')} autoFocus maxLength={120}
                onChange={(e) => poser('materiau', e.target.value)} style={{ ...inputStyle, width: '100%' }} />
              {proxActuelle?.identique && (
                <div role="alert" style={{ marginTop: 6, fontSize: 12.5, color: 'var(--amber-800)', background: 'var(--amber-50)', borderRadius: 'var(--radius-sm)', padding: '6px 9px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>Ce produit existe déjà : <strong>{proxActuelle.identique.libelle}</strong>.</span>
                  <Bouton variante="primaire" taille="sm" type="button" onClick={() => choisirProduit(proxActuelle.identique!.cle)}>Choisir « {proxActuelle.identique.libelle} »</Bouton>
                </div>
              )}
              {proxActuelle && !proxActuelle.identique && proxActuelle.proches.length > 0 && (
                <div role="alert" style={{ marginTop: 6, fontSize: 12.5, color: 'var(--amber-800)', background: 'var(--amber-50)', borderRadius: 'var(--radius-sm)', padding: '6px 9px', display: 'grid', gap: 6 }}>
                  <span>
                    Un produit proche est déjà au registre. Deux graphies du même produit feraient deux stocks et deux
                    alertes de rupture : choisissez-le si c’est le même.
                  </span>
                  {proxActuelle.proches.map((x) => (
                    <span key={x.cle} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <strong>{x.libelle}</strong><span style={aide}>({x.raison})</span>
                      <Bouton variante="primaire" taille="sm" type="button" onClick={() => choisirProduit(x.cle)}>Choisir</Bouton>
                    </span>
                  ))}
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--text-body)' }}>
                    <input type="checkbox" checked={autreProduit} onChange={(e) => setAutreProduit(e.target.checked)} />
                    C’est un autre produit : créer « {libelleNouveau} »
                  </label>
                </div>
              )}
            </div>
            <div>
              <label style={lbl} htmlFor="rec-discipline">Discipline</label>
              <select id="rec-discipline" value={c('discipline')} onChange={(e) => poser('discipline', e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                <option value="">— non renseignée —</option>
                {disciplines.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl} htmlFor="rec-passage">Passage du contrôle</label>
              <select id="rec-passage" value={c('passage')} onChange={(e) => poser('passage', e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                <option value="">— non renseigné —</option>
                {rythmes.map((r) => <option key={r.valeur} value={r.valeur}>{r.libelle}</option>)}
              </select>
              <div style={{ ...aide, marginTop: 4 }}>
                Écrit sur le flacon. La prévision de rupture lit le rythme réglé au Paramétrage → Validité des matériaux.
              </div>
            </div>
          </>
        )}
        <div>
          <label style={lbl} htmlFor="rec-niveau">Niveau</label>
          <input id="rec-niveau" list="rec-niveaux" value={niveau} style={{ ...inputStyle, width: '100%' }}
            onChange={(e) => { setNiveau(e.target.value); if (p?.derniersFlacons?.[e.target.value]) preremplir(p, e.target.value); }} />
          <datalist id="rec-niveaux">{(p?.niveaux || []).map((n) => <option key={n.niveau} value={n.niveau} />)}</datalist>
        </div>
        {champ('numeroLot', 'Lot')}
        {champ('peremption', 'Péremption du lot', 'date')}
        {champ('dateReconstitution', 'Reconstitué le', 'date')}
        {champ('dateOuverture', 'Kit ouvert le', 'date')}
        {champ('numeroFlacon', 'N° de flacon (ex. 3/12)')}
        <div>
          <label style={lbl} htmlFor="rec-refFournisseur">Référence fournisseur</label>
          <input id="rec-refFournisseur" value={c('refFournisseur')} onChange={(e) => changerRef(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
        </div>
        {champ('peremptionApresReconstitution', 'Péremption après reconstitution', 'date')}
        {champ('codeBoxystock', 'Code Boxystock / emplacement')}

        {/* ── La notice : propriétés du produit, saisies avec le flacon ── */}
        <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border-subtle)', paddingTop: 10, display: 'grid', gap: 4 }}>
          <strong style={{ fontSize: 13 }}>Notice du produit</strong>
          {nouveau ? (
            <span role="note" style={{ fontSize: 12.5, color: 'var(--amber-800)', background: 'var(--amber-50)', borderRadius: 'var(--radius-sm)', padding: '5px 9px' }}>
              Produit nouveau : saisissez les valeurs depuis la notice.
            </span>
          ) : modele ? (
            <span role="note" style={{ fontSize: 12.5, color: 'var(--amber-800)', background: 'var(--amber-50)', borderRadius: 'var(--radius-sm)', padding: '5px 9px' }}>
              Repris du flacon <strong>{modele.ref}</strong> du {fmtDate(modele.dateReconstitution)} — vérifiez sur la notice.
            </span>
          ) : (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              Aucun flacon précédent de ce produit et de cette référence fournisseur ne porte ces valeurs : saisissez-les depuis la notice.
            </span>
          )}
        </div>
        {champ('nombreAliquots', 'Nombre d’aliquots', 'number')}
        {champ('volumeParUnite', 'Volume d’un aliquot')}
        <div>
          <label style={lbl} htmlFor="rec-temperatureAvant">Conservation avant reconstitution</label>
          <select id="rec-temperatureAvant" value={c('temperatureAvant')} onChange={(e) => poser('temperatureAvant', e.target.value)} style={{ ...inputStyle, width: '100%' }}>
            <option value="">— non renseignée —</option>
            {TEMPERATURES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl} htmlFor="rec-temperatureApres">Conservation après reconstitution</label>
          <select id="rec-temperatureApres" value={c('temperatureApres')} onChange={(e) => poser('temperatureApres', e.target.value)} style={{ ...inputStyle, width: '100%' }}>
            <option value="">— non renseignée —</option>
            {TEMPERATURES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {champ('volumeReconstitutionUl', 'Volume de reconstitution (µL)')}
        {champ('stabiliteCongeleJours', 'Stabilité congelé (jours)')}
        {champ('stabiliteDecongeleHeures', 'Stabilité après décongélation (heures)')}
        <div>
          <label style={lbl} htmlFor="rec-usageAliquot">Usage d’un aliquot</label>
          <select id="rec-usageAliquot" value={c('usageAliquot')} onChange={(e) => poser('usageAliquot', e.target.value)} style={{ ...inputStyle, width: '100%' }}>
            <option value="">— non renseigné (unique) —</option>
            <option value="unique">unique</option>
            <option value="multiple">multiple (dans sa stabilité)</option>
          </select>
        </div>
        {/* ── OÙ PARTENT LES ALIQUOTS — un FAIT, pas une prescription ──
            Hors du bloc « Notice du produit », et c'est volontaire : ce qui
            est au-dessus est ce que le fabricant dit, ceci est ce que le
            laboratoire fait. Les ranger ensemble les ferait confondre. */}
        {Number(c('nombreAliquots') || 0) > 0 && (
          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border-subtle)', paddingTop: 10, display: 'grid', gap: 4 }}>
            <strong style={{ fontSize: 13 }}>Conservation des aliquots</strong>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              Le congélateur où ces tubes partent réellement — pas ce que prescrit la notice.
            </span>
          </div>
        )}
        {Number(c('nombreAliquots') || 0) > 0 && (
          <div>
            <label style={lbl} htmlFor="rec-temperatureAliquots">Où partent les aliquots</label>
            <select id="rec-temperatureAliquots" value={c('temperatureAliquots')} onChange={(e) => poser('temperatureAliquots', e.target.value)} style={{ ...inputStyle, width: '100%' }}>
              <option value="">— non renseignée —</option>
              {TEMPERATURES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        )}
        {/* L'ÉCART est DIT, pas refusé : aliquoter et congeler un contrôle que
            la notice range à 2 – 8 °C est la pratique normale du laboratoire,
            couverte par la stabilité congelé de cette même notice. Un écart
            qu'on voit est un écart qu'on peut justifier ; un écart muet est
            celui qu'un évaluateur trouve à votre place. */}
        {c('temperatureAliquots').trim() && c('temperatureApres').trim()
          && c('temperatureAliquots') !== c('temperatureApres') && (
          <div style={{ gridColumn: '1 / -1', fontSize: 12.5, color: 'var(--amber-700)' }}>
            La notice range ce réactif à
            {' '}<strong>{TEMPERATURES.find(([v]) => v === c('temperatureApres'))?.[1]}</strong>
            {' '}après reconstitution, et ces aliquots partent à
            {' '}<strong>{TEMPERATURES.find(([v]) => v === c('temperatureAliquots'))?.[1]}</strong>.
            {' '}C’est courant et ce n’est pas un refus — la stabilité congelé de la notice le couvre.
            L’écart est seulement écrit, pour qu’il puisse être justifié.
          </div>
        )}
        {!c('stabiliteCongeleJours').trim() && Number(c('nombreAliquots') || 0) > 0 && (
          <div style={{ gridColumn: '1 / -1', fontSize: 12.5, color: 'var(--amber-700)' }}>
            Sans stabilité congelé, la DLU des aliquots sera la seule péremption du lot — et l’écran dira que la stabilité manque.
          </div>
        )}
        {champ('remarque', 'Remarque', 'text', true)}
      </div>
      {erreur && <div role="alert" style={{ marginTop: 10, color: 'var(--red-600)', fontSize: 13 }}>{erreur}</div>}
    </Modale>
  );
}

/* ── Étiquettes : Zebra (ZPL) ou planche A4 ── */
/**
 * ÉTIQUETER UN OU PLUSIEURS LOTS.
 *
 * ⚠️ ON IMPRIME UN LOT, PLUS UNE LISTE DE TUBES (RQ, 05/10/2026). Les libellés
 * d'un lot sont identiques : il n'y a rien à cocher tube par tube, seulement
 * un nombre — et par défaut c'est ce qui reste au congélateur.
 *
 * ⚠️ UNE REQUÊTE PAR LOT, et c'est voulu : une rangée ne mélange jamais deux
 * lots (`zplLotIdentique`), sinon il faudrait regarder ce qu'on découpe.
 */
function Etiqueter({ lots, etat, onClose }: { lots: LotVue[]; etat: EtatAliquots; onClose: () => void }) {
  const zebra = etat.etiquettes.zebraAdmise.ok;
  /* « La Zebra sera installée sur l'ordinateur qui édite les étiquettes »
     (RQ, 11/10/2026) : le poste d'abord, par l'agent Zebra Browser Print ;
     l'envoi réseau par le moteur reste pour une Zebra du réseau. */
  const [support, setSupport] = useState<Support>('poste');
  const [poste, setPoste] = useState<Detection | null>(null);
  useEffect(() => { let v = true; void imprimanteDuPoste().then((d) => { if (v) setPoste(d); }); return () => { v = false; }; }, []);
  const [caseDepart, setCaseDepart] = useState('1');
  const [motif, setMotif] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const total = lots.reduce((n, l) => n + l.restants, 0);
  const g = etat.etiquettes.planche;
  const parPage = g.lignes * g.colonnes;
  /* Le motif n'est exigé que si l'un des lots a DÉJÀ été étiqueté — c'est le
     moteur qui tranche, l'écran ne fait que l'anticiper pour griser le bouton. */
  const motifManquant = lots.some((l) => l.crees > 0 && l.restants < l.crees) && motif.trim().length < 5;

  const imprimer = async () => {
    setMessage(''); setEnvoi(true);
    try {
      const depart = Number(caseDepart);
      if (support === 'a4') {
        const pos = positionsPlanche(total, g, depart);
        if (pos.erreur) { setMessage(pos.erreur); return; }
      }
      const faits: string[] = [];
      for (const l of lots) {
        const r = await fetchEcriture('/api/ged/ged/aliquots/etiquettes', {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ flaconRef: l.flaconRef, support, motif: motif.trim() || undefined }),
        });
        const d = await r.json().catch(() => null);
        if (!r.ok) { setMessage(d?.error?.message || 'Impression refusée.'); return; }
        if (support === 'poste') {
          const r2 = await imprimerSurLePoste(d.zpl);
          if (!r2.ok) { setMessage(r2.raison); return; }
          faits.push(`${l.produit} ${niveauCourt(l.niveau)} → ${r2.imprimante}`);
        }
        if (support === 'zpl') telechargerZpl(d.zpl, `lot-${l.codeLot || l.flaconRef}-${aujourdhuiIso()}.zpl`);
        /* Sur planche A4, les libellés d'un lot sont IDENTIQUES : on répète le
           même contenu autant de fois qu'il y a de tubes. */
        if (support === 'a4') {
          await planchePdf(Array.from({ length: d.libelles }, () => d.contenu), g, depart, `planche-${l.codeLot || l.flaconRef}-${aujourdhuiIso()}.pdf`);
        }
        if (d.avertissements?.length) faits.push(d.avertissements.join(' '));
        if (d.surplus > 0) faits.push(`${d.libelles} libellés pour ${d.libelles - d.surplus} tube(s) : une rangée ne se coupe pas en deux.`);
      }
      setMessage(support === 'poste' ? faits.join(' · ')
        : support === 'zebra' ? 'Envoyé à l’imprimante du réseau.' : `Fichier prêt. ${faits.join(' ')}`);
    } finally { setEnvoi(false); }
  };

  return (
    <Modale titre={`Étiquettes — ${lots.length} lot(s), ${total} tube(s)`} largeur={560} onClose={onClose}
      pied={<>
        <PiedActions taille="sm" annuler={{ libelle: 'Fermer', onClick: onClose }}
          principale={{ libelle: LIBELLE_SUPPORT[support], chargement: envoi, disabled: envoi || motifManquant || (support === 'poste' && !poste?.ok), title: motifManquant ? 'Saisissez le motif de réimpression' : support === 'poste' && !poste?.ok ? 'Aucune Zebra joignable sur ce poste' : undefined, onClick: () => void imprimer() }} />
      </>}>
      <div style={{ display: 'grid', gap: 12, fontSize: 13 }}>
        <ChoixSupport support={support} onChange={setSupport} poste={poste} zebraReseau={{ ok: zebra, raison: etat.etiquettes.zebraAdmise.raison }} a4Verifie={Boolean(etat.etiquettes.planche.verifieLe)} />
        {support !== 'a4' && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Étiquette {etat.etiquettes.largeurMm} × {etat.etiquettes.hauteurMm} mm, {etat.etiquettes.dpi} dpi.</span>}
        {support === 'a4' && (
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Repli quand la Zebra est indisponible. La feuille est <strong>entièrement autocollante,
            sans prédécoupe</strong> : les traits de coupe sont donc imprimés autour de chaque
            étiquette. Imprimez à 100 %, sans « ajuster à la page » — un ajustement automatique
            décale la grille et les traits ne tombent plus où il faut.
          </p>
        )}
        {support === 'a4' && (
          <div>
            <label style={lbl} htmlFor="etq-depart">Commencer à la case (planche entamée)</label>
            <input id="etq-depart" type="number" min={1} max={parPage} value={caseDepart} onChange={(e) => setCaseDepart(e.target.value)}
              style={{ ...inputStyle, width: 90 }} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginInlineStart: 8 }}>
              1 = en haut à gauche, ligne par ligne ; {g.lignes} × {g.colonnes} = {parPage} par planche.
            </span>
          </div>
        )}
        {motifManquant && (
          <div>
            <label style={lbl} htmlFor="etq-motif">Ce lot a déjà été étiqueté — motif de réimpression (consigné au journal)</label>
            <input id="etq-motif" value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="ex. étiquette décollée au congélateur"
              style={{ ...inputStyle, width: '100%' }} />
            {motifManquant && <div style={{ ...aide, marginTop: 4 }}>Saisissez le motif de réimpression (5 caractères au moins) pour imprimer.</div>}
          </div>
        )}
        {message && <div role="status" style={{ color: 'var(--text-body)' }}>{message}</div>}
      </div>
    </Modale>
  );
}

/** Ce qu'une étiquette porte. ⚠️ `lignes` est calculé PAR LE MOTEUR
 *  (`lignesEtiquette`, backend/src/etiquettes-zpl.ts) et servi tel quel : la
 *  planche A4 ne recompose pas le texte. Deux endroits qui composent la même
 *  étiquette finissent par ne plus dire la même chose — et l'écart ne se voit
 *  qu'au congélateur, des mois plus tard. */
export type Contenu = {
  code: string; produit: string; niveau: string; lot: string;
  reconstitution: string; limite: string; lignes: string[];
};
export type Support = 'poste' | 'zebra' | 'zpl' | 'a4';
export const LIBELLE_SUPPORT: Record<Support, string> = { poste: 'Imprimer sur la Zebra de ce poste', zebra: 'Imprimer sur la Zebra du réseau', zpl: 'Télécharger le ZPL', a4: 'Planche A4 autocollante' };

/** Le choix du support — partagé par les aliquots et les fractions des lots.
 *  Ce qui est indisponible le DIT (agent absent, site non accepté, adresse
 *  réseau non réglée) : un bouton grisé sans phrase est un piège déjà payé. */
export function ChoixSupport({ support, onChange, poste, zebraReseau, a4Verifie = true }: { support: Support; onChange: (s: Support) => void; poste: Detection | null; zebraReseau: { ok: boolean; raison?: string }; a4Verifie?: boolean }) {
  return (
    <>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <label><input type="radio" checked={support === 'poste'} onChange={() => onChange('poste')} /> Zebra de ce poste</label>
        <label><input type="radio" checked={support === 'zebra'} disabled={!zebraReseau.ok} onChange={() => onChange('zebra')} /> Zebra du réseau (par le moteur)</label>
        <label><input type="radio" checked={support === 'zpl'} onChange={() => onChange('zpl')} /> Fichier ZPL</label>
        <label><input type="radio" checked={support === 'a4'} onChange={() => onChange('a4')} /> Planche A4</label>
      </div>
      {support === 'poste' && (
        <span style={{ fontSize: 12, color: poste?.ok ? 'var(--green-700)' : 'var(--text-muted)' }}>
          {poste === null ? 'Recherche de la Zebra sur ce poste…'
            : poste.ok ? `Imprimante trouvée : ${poste.imprimante.name}${poste.imprimante.connection ? ` (${poste.imprimante.connection})` : ''}.`
              : <>{poste.raison}{poste.installer && <> <a href={LIEN_BROWSER_PRINT} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary-active)' }}>Télécharger Zebra Browser Print</a>.</>}</>}
        </span>
      )}
      {support === 'a4' && !a4Verifie && (
        <div role="note" style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong>Code non vérifié à la douchette.</strong> Le DataMatrix de la planche est dessiné par l’écran, pas par l’imprimante : personne ne l’a encore relu avec une douchette. Scannez une étiquette de la planche avant de coller ; le jour de l’essai se consigne au paramétrage (Aliquots › Planches A4) et retire cet avertissement.
        </div>
      )}
      {support === 'zebra' && !zebraReseau.ok && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Envoi réseau indisponible : {zebraReseau.raison}</span>}
    </>
  );
}

export function telechargerZpl(zpl: string, nom: string) {
  const url = URL.createObjectURL(new Blob([zpl], { type: 'application/octet-stream' }));
  const a = document.createElement('a'); a.href = url; a.download = nom; a.click();
  URL.revokeObjectURL(url);
}

/** La planche A4 en PDF vectoriel (jsPDF, déjà dans le projet). Le texte et le
 *  code-barres reprennent EXACTEMENT le contenu de l'étiquette Zebra (servi par
 *  le moteur), dans la case calculée par `positionsPlanche`. */
/**
 * LA PLANCHE A4 — LA MÊME ÉTIQUETTE QUE LA ZEBRA.
 *
 * ── La demande (RQ, 11/10/2026) ────────────────────────────────────────────
 *
 * « Je voudrais que les étiquettes du PDF soient identiques en contenu et en
 * taille à celles de la Zebra », et « je veux que la solution soit la même
 * pour les utilisateurs ». Un repli qui produit une AUTRE étiquette n'est pas
 * un repli : c'est un second format, qu'il faut connaître, qui s'apprend en
 * double, et qui diverge le jour où l'on touche à l'autre.
 *
 * Donc : 40 × 10 mm, DataMatrix de 7 mm à 1,5 mm des bords, les trois mêmes
 * lignes — produit, niveau + lot, les deux dates. Au millimètre près.
 *
 * ── Ce qui diffère, et qui ne pouvait pas faire autrement ──────────────────
 *
 * Le DataMatrix est construit ICI (`datamatrix.ts`) et non par le firmware de
 * l'imprimante. ⚠️ Cet encodeur N'A PAS ÉTÉ RELU PAR UNE MACHINE — aucun
 * décodeur n'est installé. Il passe 25 contrôles d'invariants, dont la
 * relecture des mots depuis la matrice, mais la vérification reste LA
 * DOUCHETTE sur une planche imprimée. Tant que ce n'est pas fait, le repli
 * est une promesse.
 *
 * ── Les traits de coupe ────────────────────────────────────────────────────
 *
 * ⚠️ La feuille est ENTIÈREMENT AUTOCOLLANTE, sans prédécoupe (RQ) : sans
 * trait imprimé, rien ne guide les ciseaux. Dessinés AVANT le contenu — un
 * trait par-dessus un code le rendrait illisible, et ça ne se voit qu'à la
 * douchette, une fois les tubes au congélateur.
 */
export async function planchePdf(etiquettes: Contenu[], g: EtatAliquots['etiquettes']['planche'], depart: number, nomFichier: string) {
  const { jsPDF } = await import('jspdf');
  const { datamatrix } = await import('@/lib/datamatrix');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const { pages } = positionsPlanche(etiquettes.length, g, depart);
  /* Les cotes de l'étiquette Zebra, en dur ici parce qu'elles SONT
     l'étiquette : les changer séparément recréerait les deux formats qu'on
     vient de fondre en un. */
  const MARGE = 1.5; const COTE_DM = 7;
  const tiret = (dessus: boolean) => {
    if (typeof (doc as unknown as { setLineDashPattern?: unknown }).setLineDashPattern === 'function') {
      (doc as unknown as { setLineDashPattern: (p: number[], o: number) => void }).setLineDashPattern(dessus ? [0.8, 0.8] : [], 0);
    }
  };
  pages.forEach((page, i) => {
    if (i > 0) doc.addPage();
    // 1. Les traits de coupe d'abord.
    doc.setDrawColor(150); doc.setLineWidth(0.1); tiret(true);
    for (const c of page) doc.rect(c.xMm, c.yMm, g.largeurMm, g.hauteurMm, 'S');
    tiret(false); doc.setDrawColor(0);
    // 2. Puis le contenu, identique au ZPL.
    for (const c of page) {
      const e = etiquettes[c.etiquette];
      if (!e) continue;
      const x = c.xMm; const y = c.yMm;
      // Le DataMatrix, centré sur la hauteur comme sur la Zebra.
      const m = datamatrix(e.code);
      const pas = COTE_DM / m.length;
      const dmY = y + (g.hauteurMm - COTE_DM) / 2;
      for (let r = 0; r < m.length; r += 1) {
        for (let k = 0; k < m.length; k += 1) {
          if ((m[r] as boolean[])[k]) doc.rect(x + MARGE + k * pas, dmY + r * pas, pas * 1.05, pas * 1.05, 'F');
        }
      }
      /* Les trois lignes viennent du MOTEUR (`lignes`, calculées par
         `lignesEtiquette`) : l'ordre des mentions et la troncature ne se
         rejouent pas ici. Deux endroits qui composent le même texte finissent
         par ne plus dire la même chose. */
      const tx = x + MARGE + COTE_DM + MARGE;
      const mm = (pt: number) => pt * 25.4 / 72;
      const lignes = e.lignes || [];
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.1);
      doc.text(String(lignes[0] ?? ''), tx, y + 1.4 + mm(7.1));
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6);
      doc.text(String(lignes[1] ?? ''), tx, y + 4.2 + mm(6));
      doc.setFontSize(5.7);
      doc.text(String(lignes[2] ?? ''), tx, y + 6.9 + mm(5.7));
    }
  });
  doc.save(nomFichier);
}

/* ── Jeter : toujours avec un motif ── */
function Jeter({ aliquots, onClose, onFait }: { aliquots: AliquotVue[]; onClose: () => void; onFait: () => void }) {
  const [motif, setMotif] = useState(aliquots.every((a) => a.dansStabilite === false) ? 'Périmé / fin de stabilité' : '');
  const [erreur, setErreur] = useState('');
  const envoyer = async () => {
    const r = await fetchEcriture('/api/ged/ged/aliquots/jeter', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ documentIds: aliquots.map((a) => a.documentId), motif: motif.trim() }),
    });
    const d = await r.json().catch(() => null);
    if (!r.ok) { setErreur(d?.error?.message || 'Refusé.'); return; }
    onFait();
  };
  return (
    <Modale titre={`Jeter ${aliquots.length} aliquot(s)`} largeur={520} onClose={onClose}
      pied={<>
        <PiedActions taille="sm" annuler={{ onClick: onClose }}
          principale={{ libelle: 'Jeter', variante: 'danger', disabled: motif.trim().length < 5, title: motif.trim().length < 5 ? 'Saisissez le motif pour jeter' : undefined, onClick: () => void envoyer() }} />
      </>}>
      <div style={{ display: 'grid', gap: 10, fontSize: 13 }}>
        <div>{aliquots.map((a) => a.codeLisible).join(', ')}</div>
        <label style={lbl} htmlFor="jet-motif">Motif (obligatoire, consigné au journal)</label>
        <input id="jet-motif" value={motif} onChange={(e) => setMotif(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
        {motif.trim().length < 5 && <span style={aide}>Saisissez le motif pour jeter (5 caractères au moins).</span>}
        {erreur && <div role="alert" style={{ color: 'var(--red-600)' }}>{erreur}</div>}
      </div>
    </Modale>
  );
}
